import { Ajax } from "../../api/infra";
import { Dates, OData, ODataQueryOptions } from "../../util";
import { CalculationRowSource } from "../../model";
import type { Calculation } from "../../model";
import type { InputEnumOption } from "../../ui";
import { WorktimeImportStatus } from "./model";
import type { WorktimeImport, WorktimeImportRow, WorktimeImportInStagingListItem } from "./model";
import { StagingApiConnector } from "./StagingApiConnector";
import { Payrolls } from "../../api";

/** Connects to Worktime service in the staging server */
export class WorktimeApiConnector extends StagingApiConnector<WorktimeImport, WorktimeImportInStagingListItem> {

  /** The current rows filtered by current payroll */
  public filteredRows: WorktimeImportRow[] | undefined = [];

  /** Manually editable rows */
  public editableRows: WorktimeImportRow[] | undefined = [];

  /** Number of rows that are outside the already created Payrolls. */
  public rowsOutsidePayrollCount: number = 0;

  /**
   * List of payrolls (metadata) available in the system.
   * Undefined if the payrolls have not yet been fetched.
   */
  public payrolls: InputEnumOption[] | undefined = undefined;

  private _detailsPayrollId: string | undefined = undefined;

  constructor(serviceUrl: string, ajax: Ajax) {
    super(serviceUrl, ajax, WorktimeApiConnector.getBlank);
  }

  /**
   * Current payroll id in the details view (typically employment relations specific, but may carry out from one employment to another when swtiching).
   * This is different from payrollId which is the payroll id in the list view.
   */
  public get detailsPayrollId(): string | undefined {
    return this._detailsPayrollId;
  }
  public set detailsPayrollId(value: string | undefined) {
    this._detailsPayrollId = value;
    this.setFiltersForDetailsPayroll();
  }

  /**
   * Gets the current payroll (metadata) in details view based on the detailsPayrollId.
   * @returns The payroll metadata if found or undefined.
   */
  public get detailsPayroll(): InputEnumOption | undefined {
    return this.payrolls?.find(x => x.value == this.detailsPayrollId);
  }

  /** Current calc is the main preview calculation if available. */
  public get currentCalc(): Calculation | undefined {
    if (!this.current?.preview?.calculations?.length || !this.detailsPayrollId) {
      return undefined;
    }
    return this.current.preview.calculations.find(x => x.info?.payrollId == this.detailsPayrollId);
  }

  /**
   * Gets the worktime imports filtered by payroll.
   * @param payrollId - The payroll id to filter by.
   * @returns The filtered list of worktime imports.
   */
  public getFilteredList(payrollId: string | undefined): WorktimeImportInStagingListItem[] {
    if (!payrollId) {
      return this.list;
    }
    return this.list.filter(x => x.payrolls[payrollId]);
  }

  /**
   * Fetches all payrolls and stores them in the payrolls property.
   * The calling components should typically call this method in init.
   * It is not in the constructor because AngularJS needs to get the promise and call timeout to get the UI to update.
   */
  public async fetchAllPayrolls(): Promise<void> {
    const payrolls = await OData.getAllItems((options: ODataQueryOptions) => {
      return new Payrolls(this.ajax).getOData(options);
    }, {});
    this.payrolls = payrolls.map(x => ({
      value: x.id,
      text: x.data?.title,
      ui: {
        start: x.data?.periodStart,
        end: x.data?.periodEnd,
        date: x.salaryDate,
        calcCount: x.data?.calcCount,
        // Not sure if this is really needed.
        rangeText: Dates.getFormattedRange(x.data?.periodStart || this.current?.header?.period?.start || "1900-01-01", x.data?.periodEnd || this.current?.header?.period?.end || "2100-01-01"),
      }
    })) as any;
    this.payrolls?.splice(0, 0, {
      value: "unknown",
      text: "Rivit palkkalistojen ulkopuolella",
      ui: {
        start: Dates.getToday(),
        end: Dates.getToday(),
        date: Dates.getToday(),
        calcCount: 0,
        rowCount: 0,
        previewCalcId: undefined,
        previewCalcRowCount: 0,
      }
    });
    this.payrolls = this.payrolls?.sort((a, b) => (b.ui?.start ?? "").localeCompare(a.ui?.start ?? ""));
  }

  /**
   * Marks the selected rows with given state.
   * @param rows - The rows to mark.
   * @param status - The status to set.
   * Status "new" is essentially reverting the state from "ignored" => final state may be "preview", "new" or "error" depending on other properties of the row.
   */
  public async setStatus(rows: WorktimeImportRow[], status: "ignored" | "new"): Promise<WorktimeImport> {
    if (!this.current?.id) {
      throw new Error("No current item to update status for.");
    }
    const result = await this.ajax.postJSON(this.serviceUrl + "/update-worktime-status", {
      id: this.current.id,
      status,
      rows,
    }) as WorktimeImport;
    this.setCurrent(result);
    return result;
  }

  /**
   * Moves the selected rows to the given payroll.
   * @param rows - The rows to move.
   * @param payrollId - The payroll id to move to.
   * @returns The updated worktime import.
   */
  public async moveToPayroll(rows: WorktimeImportRow[], payrollId: string): Promise<WorktimeImport> {
    if (!this.current?.id) {
      throw new Error("No current item to update status for.");
    }
    if (!payrollId || !rows.length) {
      throw new Error("Payroll ID and rows are required.");
    }
    const result = await this.ajax.postJSON(this.serviceUrl + "/move-to-payroll", {
      id: this.current.id,
      payrollId,
      rows,
    }) as WorktimeImport;
    this.setCurrent(result);
    return result;
  }

  /** Sets the current item in the UI. */
  public setCurrent(item: WorktimeImport | undefined): void {
    super.setCurrent(item);
    if (!item?.preview?.calculations?.length) {
      this.rowsOutsidePayrollCount = 0;
      this.setFiltersForDetailsPayroll();
      return;
    }
    this.rowsOutsidePayrollCount = item.rows?.filter(x => (x.data?.payrollId || "unknown") == "unknown").length ?? 0;
    this.payrolls?.forEach(x => {
      if (x.value == "unknown") {
        x.text = `Rivit palkkalistojen ulkopuolella`;

        x.ui.rowCount = this.rowsOutsidePayrollCount;
        x.ui.previewCalcId = undefined;
        x.ui.previewCalcRowCount = 0;
      } else {
        x.ui.rowCount = item.rows?.filter(y => y.data?.payrollId == x.value).length ?? 0;
        const calc = item.preview?.calculations?.find(y => y.info?.payrollId == x.value);
        x.ui.previewCalcId = calc?.id;
        x.ui.previewCalcRowCount = calc?.rows?.length ?? 0;
      }

    });
    this.setFiltersForDetailsPayroll();
  }

  /**
   * Client-side (synchronous) method for getting a new blank item as bases for UI binding.
   * TODO: Move to helpers.
   */
  public static getBlank(): WorktimeImport {
    return {
      rows: [],
      files: [],
      header: {
        period: {},
        status: WorktimeImportStatus.Uploaded,
      },
      usecase: {
        data: {},
      },
      workflowData: {
        events: [],
      },
    };
  }

  /**
   * Recalculates a worktime item in storage.
   * @param id Identifier of the worktime item to recalculate. If none, is specified recaculates the current (current is also set).
   * @returns The recalculated worktime item or undefined if not found / no current & id.
   */
  public async recalculateWorktime(id?: string): Promise<WorktimeImport | undefined> {
    const setCurrent = !id;
    id = (id || this.current?.id) ?? undefined;
    if (!id) {
      return undefined;
    }
    const result = await this.ajax.postJSON(this.serviceUrl + "/recalculate/" + id, {});
    if (setCurrent) {
      this.setCurrent(result);
    }
    return result;
  }

  /**
   * Saves the manual rows to the server.
   * @returns The updated worktime import.
   */
  public async saveManualRows(): Promise<void> {
    if (!this.detailsPayrollId) {
      throw new Error("No payroll selected for manual rows.");
    }
    this.current ??= {};
    this.current.rows ??= [];
    this.current.rows = this.current.rows.filter(x => x.source != CalculationRowSource.ManualRow);
    this.editableRows?.forEach(x => {
      x.employmentId = this.current?.id;

      x.source = CalculationRowSource.ManualRow;
      x.status = "edited";
      x.data ??= {};
      x.data.payrollId = this.detailsPayrollId;
      x.period = x.period || {};
      x.period.start = x.period.start || this.detailsPayroll?.ui?.start;
      x.period.end = x.period.end || this.detailsPayroll?.ui?.start;
      this.current?.rows?.push(x);

    });
    this.setFiltersForDetailsPayroll();
    await this.saveCurrent();
  }

  private setFiltersForDetailsPayroll() {
    if (!this.current) {
      this.filteredRows = [];
      this.editableRows = [];
      return;
    }
    if (this.detailsPayrollId) {
      this.filteredRows = this.current.rows?.filter(x => x.data?.payrollId == this.detailsPayrollId);
      this.editableRows = this.filteredRows?.filter(x => x.source == CalculationRowSource.ManualRow);
      return;
    }
    this.filteredRows = this.current.rows?.filter(x => !x.data?.payrollId);
    this.editableRows = [];
  }

}